import React, { useCallback, useEffect, useState } from "react";
import { useVisitedPageCache, visitedPageKey } from "../../utils/visitedPageCache";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  listCampaigns,
  deleteCampaign,
  exportCampaignCsv,
  getWhatsAppErrorMessage,
  unwrapWhatsAppList,
} from "../../services/whatsappApi";
import {
  CAMPAIGN_STATUS_OPTIONS,
  campaignProgressLabel,
  campaignProgressPercent,
  campaignStatusClass,
  downloadBlob,
  formatCampaignDateList,
} from "../../utils/whatsappCampaign";
import "./whatsapp.css";
import "./whatsapp-campaigns-list.css";
import WhatsAppPageShell from "./WhatsAppPageShell";

const PAGE_SIZE = 10;

function reportError(err, fallback) {
  const msg = getWhatsAppErrorMessage(err, fallback);
  toast.error(msg);
  return msg;
}

function StatusBadge({ status }) {
  return (
    <span className={`wa-status-badge ${campaignStatusClass(status)}`}>
      {status || "—"}
    </span>
  );
}

function campaignField(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && value !== "") return value;
  }
  return null;
}

function exportRowsToCsv(rows) {
  const headers = [
    "Campaign Name",
    "Template",
    "Status",
    "Sent",
    "Delivered",
    "Read",
    "Failed",
    "Created",
  ];
  const lines = rows.map((row) => {
    const values = [
      campaignField(row, "title") || "",
      campaignField(row, "templateName", "template_name") || "",
      row.status || "",
      row.sentCount ?? row.sent_count ?? 0,
      row.deliveredCount ?? row.delivered_count ?? 0,
      row.readCount ?? row.read_count ?? 0,
      row.failedCount ?? row.failed_count ?? 0,
      formatCampaignDateList(row.createdAt ?? row.created_at),
    ];
    return values
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(",");
  });
  const csv = [headers.join(","), ...lines].join("\n");
  downloadBlob(
    new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    "campaigns.csv",
  );
}

export default function WhatsAppCampaigns() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exportingId, setExportingId] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);

  const pageCache = useVisitedPageCache();

  const fetchCampaigns = useCallback(async (force = false) => {
    const cacheKey = visitedPageKey([page, PAGE_SIZE, statusFilter, searchQuery]);
    if (force) pageCache.clear();
    const cached = force ? null : pageCache.read(cacheKey);
    if (cached) {
      setRows(cached.items);
      setTotal(cached.total);
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await listCampaigns({
        page,
        limit: PAGE_SIZE,
        status: statusFilter,
        search: searchQuery,
      });
      const data = unwrapWhatsAppList(res);
      const total = Number(res?.total ?? res?.data?.total) || data.length;
      setRows(data);
      setTotal(total);
      pageCache.write(cacheKey, { items: data, total });
    } catch (err) {
      const msg = reportError(err, "Failed to load campaigns");
      setError(msg);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, searchQuery]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE, total);

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
    setPage(1);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteCampaign(deleteTarget.id);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to delete campaign");
      }
      toast.success("Campaign deleted");
      setDeleteTarget(null);
      await fetchCampaigns(true);
    } catch (err) {
      reportError(err, "Failed to delete campaign");
    } finally {
      setDeleting(false);
    }
  };

  const handleExportRow = async (row) => {
    setExportingId(row.id);
    try {
      const blob = await exportCampaignCsv(row.id);
      const safeTitle = String(row.title || "campaign")
        .replace(/[^\w\-]+/g, "_")
        .slice(0, 40);
      downloadBlob(blob, `${safeTitle}_export.csv`);
      toast.success("Export downloaded");
    } catch (err) {
      reportError(err, "Failed to export CSV");
    } finally {
      setExportingId(null);
    }
  };

  const handleExportAll = async () => {
    if (!rows.length) {
      toast.error("No campaigns to export");
      return;
    }
    setExportingAll(true);
    try {
      exportRowsToCsv(rows);
      toast.success("Export downloaded");
    } finally {
      setExportingAll(false);
    }
  };

  return (
    <WhatsAppPageShell className="whatsapp-page-shell--panel">
      <div className="whatsapp-inbox-root">
        <div className="whatsapp-page-panel wa-campaigns-list-page">
          <header className="whatsapp-page-header">
            <div>
              <h1>My Campaign</h1>
              <p className="whatsapp-page-subtitle">
                View and manage your WhatsApp broadcast campaigns
              </p>
            </div>
            <Link
              to="/whatsapp/campaigns/send"
              className="whatsapp-btn-primary"
            >
              <Icon icon="solar:send-square-line-duotone" width={18} />
              Send Campaign
            </Link>
          </header>

          <div className="wa-page-toolbar">
            <div className="wa-page-toolbar-search">
              <input
                type="search"
                placeholder="Search campaigns..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
                aria-label="Search campaigns"
              />
              <button
                type="button"
                className="wa-page-toolbar-search-btn"
                onClick={handleSearch}
              >
                Search
              </button>
            </div>

            <div className="wa-page-toolbar-actions">
              <label className="wa-page-toolbar-filter">
                <Icon icon="solar:filter-line-duotone" width={16} />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  {CAMPAIGN_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.value ? opt.label : "All Statuses"}
                    </option>
                  ))}
                </select>
              </label>

              <button
                type="button"
                className="wa-page-toolbar-btn-outline"
                onClick={handleExportAll}
                disabled={exportingAll || loading || rows.length === 0}
              >
                <Icon icon="solar:download-minimalistic-linear" width={16} />
                Export To CSV
              </button>
            </div>
          </div>

          <div className="wa-campaigns-list-body">
            {loading ? (
              <div className="whatsapp-page-skeleton">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((k) => (
                  <div
                    key={k}
                    className="whatsapp-skeleton"
                    style={{ height: 44, marginBottom: 8 }}
                  />
                ))}
              </div>
            ) : error ? (
              <div className="whatsapp-list-error">{error}</div>
            ) : rows.length === 0 ? (
              <div className="whatsapp-empty-state">
                <Icon
                  icon="solar:letter-line-duotone"
                  width={56}
                  style={{ opacity: 0.45 }}
                />
                <p>No campaigns yet. Send your first broadcast.</p>
              </div>
            ) : (
              <div className="whatsapp-contacts-table-wrap wa-campaigns-list-table-wrap">
                <table className="whatsapp-contacts-table wa-campaigns-list-table">
                  <thead>
                    <tr>
                      <th>Campaign Name</th>
                      <th>Template</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Sent</th>
                      <th>Delivered</th>
                      <th>Read</th>
                      <th>Failed</th>
                      <th>Created</th>
                      <th>Actions</th>
                      <th aria-label="Delete" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const inProgress =
                        String(row.status).toUpperCase() === "IN_PROGRESS";
                      const progress = campaignProgressPercent(row);
                      const title = campaignField(row, "title") || "—";
                      const templateName =
                        campaignField(row, "templateName", "template_name") ||
                        "—";

                      return (
                        <tr key={row.id}>
                          <td className="wa-campaigns-list-name">{title}</td>
                          <td className="wa-campaigns-list-template">
                            {templateName}
                          </td>
                          <td>
                            <StatusBadge status={row.status} />
                          </td>
                          <td className="wa-campaigns-list-progress-cell">
                            <div className="wa-campaigns-list-progress-bar">
                              <div
                                className="wa-campaigns-list-progress-fill"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            <span className="wa-campaigns-list-progress-label">
                              {campaignProgressLabel(row)}
                            </span>
                          </td>
                          <td>{row.sentCount ?? row.sent_count ?? 0}</td>
                          <td>{row.deliveredCount ?? row.delivered_count ?? 0}</td>
                          <td>{row.readCount ?? row.read_count ?? 0}</td>
                          <td className="wa-campaigns-list-failed">
                            {row.failedCount ?? row.failed_count ?? 0}
                          </td>
                          <td>
                            {formatCampaignDateList(
                              row.createdAt ?? row.created_at,
                            )}
                          </td>
                          <td>
                            <div className="wa-campaigns-list-actions">
                              <button
                                type="button"
                                className="wa-page-toolbar-btn-outline wa-campaigns-list-details-btn"
                                onClick={() =>
                                  navigate(`/whatsapp/campaigns/${row.id}`)
                                }
                              >
                                Details
                              </button>
                              <button
                                type="button"
                                className="wa-campaigns-list-icon-btn"
                                title="Export CSV"
                                disabled={exportingId === row.id}
                                onClick={() => handleExportRow(row)}
                                aria-label={`Export ${title}`}
                              >
                                <Icon
                                  icon="solar:download-minimalistic-linear"
                                  width={18}
                                />
                              </button>
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="wa-campaigns-list-icon-btn wa-campaigns-list-icon-btn--danger"
                              disabled={inProgress}
                              title={
                                inProgress
                                  ? "Cannot delete while in progress"
                                  : "Delete campaign"
                              }
                              onClick={() => setDeleteTarget(row)}
                              aria-label={`Delete ${title}`}
                            >
                              <Icon
                                icon="solar:trash-bin-trash-line-duotone"
                                width={18}
                              />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!loading && !error && total > 0 ? (
            <footer className="wa-campaigns-list-footer">
              <span className="wa-campaigns-list-footer-count">
                Showing {rows.length} of {total} campaigns
              </span>
              <div className="wa-campaigns-list-footer-nav">
                <span className="wa-campaigns-list-range">
                  {pageStart}-{pageEnd} of {total}
                </span>
                <div className="wa-campaigns-list-page-nav">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    aria-label="Previous page"
                  >
                    <Icon icon="solar:alt-arrow-left-linear" width={16} />
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    aria-label="Next page"
                  >
                    <Icon icon="solar:alt-arrow-right-linear" width={16} />
                  </button>
                </div>
              </div>
            </footer>
          ) : null}
        </div>

        <Modal
          show={!!deleteTarget}
          onHide={() => setDeleteTarget(null)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Delete campaign</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Delete <strong>{deleteTarget?.title}</strong>? This cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </WhatsAppPageShell>
  );
}
